<template>
  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row border-bottom white-bg dashboard-header">
      <div class="col-lg-12">
        <h2>Campaigns</h2>
        <h3 class="campaign-align-title pull-right">{{ campaign.title }}</h3>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-4 interaction-container">
        <message-panel
          :id="panelSettings.uuid"
          :isPaused="!!panelPaused[panelSettings.uuid]"
          :title="'Recent Messages'"
          :type="'campaign'"
          :color="'#ee2465'"
          :canClose="false"
          :hasSettings="false"
          :interactions="interactions(panelSettings.uuid)"
          :hasSearch="false"
          :isFixedPanel="true"
          :loadingSuccess="!!loadingInteractionsSuccess[panelSettings.uuid] || false"
          :loading="loadingInteractions[panelSettings.uuid] || false"
          :hasMoreInteractions="panelHasMoreInteractions(panelSettings.uuid) || false"
          @loadMore="handleLoadMore"
          @onOpenProfile="handleOpenProfile"
          @pausePanel="pausePanel(panelSettings.uuid)"
          @unpausePanel="unpausePanel(panelSettings.uuid)"
        />
      </div>

      <div class="col-lg-8" v-if="loadingCampaignReport">
        <loader message="Loading your campaign's details..." />
      </div>

      <div class="col-lg-8 campaign-details" v-if="campaign" v-show="!loadingCampaignReport">
        <i-box>
          <span slot="title">
            <h5>Campaign Details</h5>
            <div class="ibox-tools">
              <router-link :to="{ name: 'Campaign' }" class="close-link">
                <i class="fa fa-times"></i>
              </router-link>
            </div>
          </span>
          <span slot="content">
            <div class="row campaign-details">
              <div class="col-lg-5">
                <h5 class="campaign-title">{{ campaign.title }}</h5>
                <div class="campaign-type">
                  <strong>Created on:</strong>{{ campaign.date_created | moment('YYYY-MM-DD') }}
                </div>
                <div class="campaign-type">
                  <strong>Campaign Type:</strong>
                  <span>{{ campaign.campaign_type_display }}</span>
                </div>
                <div class="campaign-type">
                  <strong>Created By:</strong>
                  <span>{{ campaign.created_by }}</span>
                </div>
                <div class="campaign-type">
                  <strong>Winning Disqualification Duration:</strong>
                  <span>
                    {{
                      convertMillisecondsToMomentDuration(
                        campaign.winning_disqualification_duration,
                      ).duration_days
                    }}
                  </span>
                </div>

                <div
                  class="campaign-channels"
                  v-for="channelLink in campaignDetails(campaign.channel_data.links)"
                  :key="channelLink.id"
                >
                  <div class="campaign-channel">
                    <label>
                      <social-icon
                        class="fa fa-fw"
                        :type="handleChannelType(channelLink.channel_type_title)"
                      ></social-icon>
                    </label>
                    <strong>{{ channelLink.handle }}:</strong>
                    <span>Keyword: "{{ channelLink.keyword }}"</span>
                  </div>
                </div>

                <div class="campaign-reply-message">
                  <strong>Reply Message:</strong>
                  <span>"{{ replyMessage }}"</span>
                </div>

                <strong v-if="campaign.top_engagers.length">Top Engagers:</strong>
                <dd class="project-people">
                  <engager
                    v-for="(profile, index) in campaign.top_engagers"
                    :key="index"
                    :profile="profile"
                    @click.native="handleSelectProfile(profile)"
                  ></engager>
                </dd>
              </div>

              <div class="col-lg-7">
                <div class="row">
                  <div class="col-lg-6">
                    <strong>Start</strong>
                    {{ campaign.start_date | moment('YYYY-MM-DD HH:mm') }}
                  </div>

                  <div class="col-lg-6">
                    <strong>End</strong>
                    <span v-if="campaign.perpetual" class="label label-primary"
                      >Perpetual Campaign</span
                    >
                    <span v-else>{{ campaign.end_date | moment('YYYY-MM-DD HH:mm') }}</span>
                  </div>
                </div>
                <div class="row campaign-breakdown-stats">
                  <div class="col-lg-2">
                    <analytics-chart
                      type="bar"
                      chartStyle="max-height: 60px"
                      :scalesEnabled="false"
                      :barPercentage="0.9"
                      :categoryPercentage="1"
                      :show-tooltips="false"
                      :data="[
                        ['total', campaignReportData.totals.total_lifetime],
                        ['unique', campaignReportData.totals.unique_lifetime],
                      ]"
                    ></analytics-chart>
                    <hr />
                    <analytics-chart
                      type="doughnut"
                      chartStyle="max-height: 60px"
                      :show-tooltips="false"
                      :data="channelTypeChartData"
                    ></analytics-chart>
                  </div>
                  <div class="col-lg-10">
                    <div class="campaign-totals pull-left">
                      <div class="total">
                        {{ campaignReportData.totals.total_lifetime.toLocaleString() }}
                      </div>
                      <div class="description">Total Interactions</div>
                    </div>
                    <div class="campaign-totals pull-right">
                      <div class="total">
                        {{ campaignReportData.totals.unique_lifetime.toLocaleString() }}
                      </div>
                      <div class="description">Total Uniques</div>
                    </div>
                    <div class="clearfix"></div>
                    <div class="campaign-channel-stats">
                      <div
                        class="row campaign-channel-stat border-top"
                        v-for="(item, key) in campaignReportData.totals
                          .total_vs_unique_channel_type"
                        v-if="item.total > 0 && item.unique > 0"
                        :key="key"
                      >
                        <div class="col-lg-4 channel-total">{{ item.total.toLocaleString() }}</div>
                        <div class="col-lg-4 channel-title">{{ formatChannelName(key) }}</div>
                        <div class="col-lg-4 channel-unique">
                          {{ item.unique.toLocaleString() }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row border-top campaign-periodic-stats">
              <div
                class="campaign-periodic-stats"
                v-if="
                  campaignReportData.totals.growth_day ||
                    campaignReportData.totals.growth_week ||
                    campaignReportData.totals.growth_month
                "
              >
                <div class="col-lg-4 campaign-periodic-stat">
                  <div class="periodic-total-block pull-left">
                    <div class="total">
                      {{ campaignReportData.totals.total_today.toLocaleString() }}
                    </div>
                    <div class="description">Received</div>
                  </div>
                  <div class="periodic-unit-block pull-right">
                    <div class="growth">
                      <growth :value="campaignReportData.totals.growth_day"></growth>
                    </div>
                    <label class="label label-success">Today</label>
                  </div>
                  <div class="clearfix"></div>
                  <div class="sparkline">
                    <trend
                      :data="processSparklineData(campaignReportData.charts.today)"
                      :gradient="['#683090', '#c97eff']"
                      :height="40"
                      :auto-draw="false"
                    ></trend>
                  </div>
                  <div class="periodic-unique-block">
                    <div class="unique">
                      {{ campaignReportData.totals.unique_today.toLocaleString() }}
                      <sup>Unique</sup>
                    </div>
                  </div>
                </div>
                <div class="col-lg-4 campaign-periodic-stat">
                  <div class="periodic-total-block pull-left">
                    <div class="total">
                      {{ campaignReportData.totals.total_this_week.toLocaleString() }}
                    </div>
                    <div class="description">Received</div>
                  </div>
                  <div class="periodic-unit-block pull-right">
                    <div class="growth">
                      <growth :value="campaignReportData.totals.growth_week"></growth>
                    </div>
                    <label class="label label-info">This Week</label>
                  </div>
                  <div class="clearfix"></div>
                  <div class="sparkline">
                    <trend
                      :data="processSparklineData(campaignReportData.charts.this_week)"
                      :gradient="['#683090', '#c97eff']"
                      :height="40"
                      :auto-draw="false"
                    ></trend>
                  </div>
                  <div class="periodic-unique-block">
                    <div class="unique">
                      {{ campaignReportData.totals.unique_this_week.toLocaleString() }}
                      <sup>Unique</sup>
                    </div>
                  </div>
                </div>
                <div class="col-lg-4 campaign-periodic-stat">
                  <div class="periodic-total-block pull-left">
                    <div class="total">
                      {{ campaignReportData.totals.total_this_month.toLocaleString() }}
                    </div>
                    <div class="description">Received</div>
                  </div>
                  <div class="periodic-unit-block pull-right">
                    <div class="growth">
                      <growth :value="campaignReportData.totals.growth_month"></growth>
                    </div>
                    <label class="label label-primary">This Month</label>
                  </div>
                  <div class="clearfix"></div>
                  <div class="sparkline">
                    <trend
                      :data="processSparklineData(campaignReportData.charts.this_month)"
                      :gradient="['#683090', '#c97eff']"
                      :height="40"
                      :auto-draw="false"
                    ></trend>
                  </div>
                  <div class="periodic-unique-block">
                    <div class="unique">
                      {{ campaignReportData.totals.unique_this_month.toLocaleString() }}
                      <sup>Unique</sup>
                    </div>
                  </div>
                </div>
              </div>
              <div class="campaign-periodic-stats no-data" v-else>
                <no-data class="label">Not enough data to display analytics</no-data>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-12 campaign-main-chart">
                <analytics-time-series-area-chart
                  :data="
                    campaignReportData.charts.channels.constructor === Array
                      ? campaignReportData.charts.channels
                      : []
                  "
                  :unit="'month'"
                  :showLegend="false"
                ></analytics-time-series-area-chart>
              </div>
            </div>
          </span>
        </i-box>
      </div>
    </div>
    <profile-detail-dialog
      v-if="profileDialogVisible && selectedProfile"
      :profile="selectedProfile"
      :profileDialogVisible="profileDialogVisible"
      :campaignId="id"
      @onDialogVisibilityChange="handleProfileVisibilityChange"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import momentDurationFormatPlugin from 'moment-duration-format';

import Trend from 'vuetrend';
import SocialIcon from '@/components/common/Icons/SocialIcon';
import ContentPlaceholder from '@/components/common/ContentPlaceholder';
import Growth from '@/components/common/Growth';
import IBox from '@/components/common/IBox';
import Loader from '@/components/common/Loader';
import NoData from '@/components/common/NoData';
import Engager from '@/components/campaign/CampaignEngager';

import ProfileDetailDialog from '@/components/profile/ProfileDetailDialog';
import AnalyticsChart from '@/components/analytics/charts/AnalyticsChart';
import AnalyticsTimeSeriesAreaChart from '@/components/analytics/charts/AnalyticsTimeSeriesAreaChart';
import MessagePanel from '@/components/interaction/MessagePanel';

import * as dashboardTypes from '@/store/modules/interaction/mutation-types';
import * as profileTypes from '@/store/modules/profile/mutation-types';

export default {
  name: 'CampaignDetail',
  components: {
    Trend,

    ContentPlaceholder,
    Growth,
    IBox,
    Loader,
    NoData,
    Engager,

    ProfileDetailDialog,
    AnalyticsChart,
    AnalyticsTimeSeriesAreaChart,
    MessagePanel,
    SocialIcon,
  },
  data: () => ({
    dates: '',
    winners: '',
    dialogVisible: false,
    selectorDateRange: '',
    randomSelectorDialogVisible: false,
  }),
  props: {
    id: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      default: () => {},
    },
    campaignReportData: {
      type: Object,
      default: () => {},
    },
    loadingCampaignReport: {
      type: Boolean,
      default: false,
    },
    icons: {
      type: Object,
      default: () => {},
    },
    channelTypes: {
      type: Object,
      default: () => {},
    },
    replyMessage: {
      type: String,
      default: '',
    },
  },
  mounted() {
    this.$store.commit(`interaction/${dashboardTypes.ADD_MANUAL_PANEL}`, this.panelSettings);
    this.$store.dispatch('interaction/fetchPanelInteractions', [this.panelSettings.uuid, null, 1]);
  },
  destroyed() {
    this.$store.commit(`interaction/${dashboardTypes.REMOVE_MANUAL_PANEL}`, this.panelSettings);
    this.$store.dispatch('interaction/clearDashboardPanelPaused');
    this.$store.commit(`profile/${profileTypes.SHOW_PROFILES_DIALOG}`, false);
  },
  computed: {
    ...mapGetters('interaction', ['interactions']),
    ...mapState('interaction', [
      'loadingInteractions',
      'hasMoreInteractions',
      'loadingInteractionsSuccess',
      'panelPaused',
    ]),
    ...mapState('profile', ['selectedProfile', 'profileDialogVisible']),

    panelSettings() {
      return {
        uuid: `campaign-panel-${this.id}`,
        type: 'campaign',
        settings: {
          name: 'Recent Messages',
          campaign_ids: [this.id],
        },
      };
    },

    channelTypeChartData() {
      const data = this.campaignReportData.totals.total_vs_unique_channel_type;
      const totals = Object.keys(data).map(k => [
        k,
        this.campaignReportData.totals.total_vs_unique_channel_type[k].total,
      ]);

      // Remove totals with 0
      return totals.filter(t => t[1] > 0);
    },

    fileName: {
      get() {
        const title = this.randomSelectorDialogVisible ? 'Random Selector' : 'Interactions';
        const data = this.campaignDetails(this.campaign.channel_data.links);
        return `-${this.campaign.title} ${data.map(
          channels => channels.channel_type_title,
        )}-${title}`;
      },
      set(newVal) {
        this.fileName = newVal;
      },
    },

    typesOfChannels: {
      get() {
        const data = this.campaignDetails(this.campaign.channel_data.links);
        return `-${data.map(types => types.handle)}`;
      },
      set(newVal) {
        this.value = newVal;
      },
    },

    isDisabled() {
      return this.dates === '';
    },

    isRandomDisabled() {
      return this.selectorDateRange === '';
    },

    hasChannelLinks() {
      return 'links' in this.campaign.channel_data;
    },
  },
  methods: {
    convertMillisecondsToMomentDuration(milliseconds) {
      moment.duration.format = momentDurationFormatPlugin;
      const duration = moment.duration(milliseconds * 1000, 'milliseconds').format('d h:m:s');
      if (duration === '0') {
        return {
          duration_days: '90',
          duration_hours: '0',
          duration_minutes: '0',
          duration_seconds: '0',
        };
      }
      return {
        duration_days: `${duration.split(' ')[0]} days`,
        duration_hours: `${duration.split(' ')[1].split(':')[0]} hours`,
        duration_minutes: `${duration.split(' ')[1].split(':')[1]} minutes`,
        duration_seconds: `${duration.split(' ')[1].split(':')[2]} seconds`,
      };
    },
    panelHasMoreInteractions(panelId) {
      return this.hasMoreInteractions[panelId] !== undefined
        ? this.hasMoreInteractions[panelId]
        : true;
    },

    getCleanName(title) {
      return title.split('|')[1] || ' -- ';
    },

    takeCampaignOffline() {
      this.showCampaignPopover = false;
      this.selectedCampaign.live = false;
      this.$message({
        message: `${this.selectedCampaign.title} is now offline`,
        type: 'success',
      });
      this.selectedCampaign = null;
    },

    formatChannelName(channelName) {
      return channelName.replace(/_/g, ' ');
    },

    processSparklineData(data) {
      return [0].concat(data.map(stat => stat.total));
    },

    campaignDetails(data) {
      if (!this.hasChannelLinks) return [];

      return data.map(c => ({
        channel_type: c.channel_type,
        channel_type_title: c.channel_type_title,
        handle: c.handle,
        keyword: c.keyword,
      }));
    },

    unpausePanel(panelId) {
      this.$store.dispatch('interaction/unpausePanelInteractions', panelId);
    },

    pausePanel(panelId) {
      this.$store.dispatch('interaction/pausePanelInteractions', panelId);
    },

    handleLoadMore() {
      this.$store.dispatch('interaction/fetchNextPageInteractions', this.panelSettings.uuid);
    },
    handleOpenProfile(originatorType, originator) {
      this.$store.dispatch('profile/searchProfileForIdentity', [originatorType, originator]);
    },
    handleProfileVisibilityChange(visibility) {
      this.$store.commit(`profile/${profileTypes.SHOW_PROFILES_DIALOG}`, visibility);
    },

    handleSelectProfile(profile) {
      this.$store.commit(`profile/${profileTypes.SELECT_PROFILES_PROFILE}`, profile);
      this.$store.commit(`profile/${profileTypes.SHOW_PROFILES_DIALOG}`, true);
    },
    handleChannelType(channelType) {
      return channelType.toLowerCase() === 'data import' ? 'Import' : channelType;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';

.no-data {
  text-align: center;
}

.campaign-details {
  margin-top: 10px;
  font-size: 11px;
  strong {
    margin-right: 6px;
    font-size: 12px;
  }
}

.campaign-title {
  color: $colony-primary;
  font-size: 14px;
  margin-bottom: 0;
}
.campaign-align-title {
  margin-top: -30px;
}
.campaign-subtitle {
  font-size: 10px;
}

.campaign-type {
  margin: 15px 0;
}

.campaign-channels {
  .campaign-channel {
    margin: 6px 0;
  }
}

.campaign-reply-message {
  margin: 15px 0;
}

.campaign-breakdown-stats {
  margin-top: 20px;
}

.campaign-totals {
  .total {
    font-size: 28px;
    font-weight: bold;
  }
  .description {
    font-size: 15px;
    font-weight: bold;
  }
}

.campaign-channel-stats {
  margin-top: 15px;

  .campaign-channel-stat {
    padding: 10px 0;

    .channel-title {
      text-align: center;
    }

    .channel-unique {
      text-align: right;
    }
  }
}

.campaign-periodic-stats {
  margin-top: 30px;

  .growth {
    font-size: 18px;
  }
}

.campaign-main-chart {
  margin-top: 40px;
}

.periodic-unit-block {
  text-align: right;
}

.periodic-total-block {
  .total {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 0;
  }
}

.periodic-unique-block {
  font-size: 23px;
  font-weight: 100;
  sup {
    font-size: 13px;
  }
}

.recent-messages {
  height: calc(100vh);
  overflow: scroll;
}

.duration-label {
  text-align: center;
}

.interaction-col {
  height: 50vh !important;
}
.social-icon {
  display: inline;
}
.campaign-download-message {
  background-color: #fff;
  padding: 10px;
  margin: auto;
  width: 50%;
}
.label-danger .label-info {
  padding: 5px;
  margin: 2px;
}
.date_range {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// CampaignDetail.vue?933b3baa